export interface CopyRichOptions {
  html: string
  plain?: string
}

export function useClipboard() {
  async function copyRich({ html, plain }: CopyRichOptions): Promise<void> {
    const normalized = normalizeHtml(html)
    const text = (plain ?? htmlToPlain(normalized)) || ''

    // Build ClipboardItem payload
    const items: Record<string, Blob> = {
      'text/html': new Blob([wrapAsFragment(normalized)], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    }

    if (isClipboardApiAvailable()) {
      const CI = (window as any).ClipboardItem ?? (window as any).webkitClipboardItem
      const item = new CI(items)
      await navigator.clipboard.write([item])
      return
    }

    // Legacy fallback
    fallbackExecCommandCopy(normalized)
  }

  async function copyHtml(html: string): Promise<void> {
    return copyRich({ html })
  }

  async function copyMarkdown(markdown: string): Promise<void> {
    // Markdown as plain text only; many apps don't parse MD on paste.
    if (navigator.clipboard && 'writeText' in navigator.clipboard) {
      await navigator.clipboard.writeText(markdown)
      return
    }
    fallbackExecCommandCopy(escapeHtml(markdown))
  }

  return { copyRich, copyHtml, copyMarkdown }
}

function isClipboardApiAvailable(): boolean {
  return !!(navigator.clipboard && 'write' in navigator.clipboard && (window as any).ClipboardItem)
}

function wrapAsFragment(innerHtml: string): string {
  // Keep minimal; many paste targets ignore <style> & class rules
  return `<div>${innerHtml}</div>`
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

export function normalizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html')
  const root = doc.getElementById('root')!

  // 1) Remove framework-specific attrs/classes; keep code language class if present
  root.querySelectorAll('[class], [style]').forEach(el => {
    if (!(el instanceof HTMLElement)) return
    if (el.classList.contains('language-html') || el.tagName === 'CODE') {
      // keep language class for potential dev pastes
    } else {
      el.removeAttribute('class')
    }
    // Strip inline styles added by the app; we'll add minimal inline styles later
    el.removeAttribute('style')
  })
  // Remove Vue scoped attrs data-v-*
  root.querySelectorAll('*').forEach(el => {
    if (!(el instanceof HTMLElement)) return
    ;[...el.attributes].forEach(attr => {
      if (/^data-v-/.test(attr.name)) el.removeAttribute(attr.name)
    })
  })

  // 2) Unwrap known containers
  root.querySelectorAll('div.agent-text').forEach(div => unwrap(div))

  // 3) Fix blank line: <li><p>Title</p><ul>…</ul></li> → <li>Title<ul>…</ul></li>
  root.querySelectorAll('li').forEach(li => {
    const first = li.firstElementChild
    if (first && first.tagName === 'P') {
      const next = first.nextElementSibling
      if (next && (next.tagName === 'UL' || next.tagName === 'OL')) {
        unwrap(first)
      }
    }
  })

  // 4) Normalize <pre><code>
  root.querySelectorAll('pre').forEach(pre => {
    const el = pre as HTMLElement
    el.setAttribute(
      'style',
      [
        'white-space:pre',
        'font-family:monospace',
        'font-size:0.95em',
        'line-height:1.4',
        'background-color:#f6f8fa',
        'border:1px solid #e1e4e8',
        'border-radius:6px',
        'padding:12px',
        'overflow:auto',
        'tab-size:4',
      ].join(';')
    )
    const code = el.querySelector('code') as HTMLElement | null
    if (code) {
      // Keep content; reduce styling so paste targets don't strip it
      code.removeAttribute('class')
      code.style.whiteSpace = 'inherit'
    }
  })

  // 5) Minimal spacing via inline style (survives most paste targets)
  root.querySelectorAll('h1,h2,h3,h4,h5,h6,p,ul,ol,pre,table').forEach(el => {
    const e = el as HTMLElement
    if (!e.getAttribute('style')) e.setAttribute('style', 'margin:0 0 8px 0')
  })

  // 6) Sanitize: keep only safe attributes
  root.querySelectorAll('*').forEach(node => {
    if (!(node instanceof HTMLElement)) return
    ;[...node.attributes].forEach(attr => {
      const name = attr.name.toLowerCase()
      const keep =
        // links
        (node.tagName === 'A' &&
          (name === 'href' || name === 'title' || name === 'rel' || name === 'target')) ||
        // table cells
        ((node.tagName === 'TD' || node.tagName === 'TH') &&
          (name === 'colspan' || name === 'rowspan')) ||
        // images (if present)
        (node.tagName === 'IMG' &&
          (name === 'src' ||
            name === 'alt' ||
            name === 'title' ||
            name === 'width' ||
            name === 'height')) ||
        // inline styles we set above
        name === 'style'
      if (!keep) node.removeAttribute(attr.name)
    })
  })

  return root.innerHTML.trim()
}

function unwrap(el: Element) {
  const parent = el.parentNode!
  while (el.firstChild) parent.insertBefore(el.firstChild, el)
  parent.removeChild(el)
}

export function htmlToPlain(html: string): string {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  const blockTags = new Set([
    'P',
    'DIV',
    'LI',
    'UL',
    'OL',
    'PRE',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'TABLE',
    'TR',
    'TD',
    'TH',
  ])
  let out = ''
  const walker = document.createTreeWalker(tmp, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT)
  let node: Node | null
  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE) out += (node as Text).data
    if (node.nodeType === Node.ELEMENT_NODE && blockTags.has((node as Element).tagName)) {
      if (!out.endsWith('\n')) out += '\n'
    }
  }
  return out.replace(/\n{3,}/g, '\n\n').trim()
}

function fallbackExecCommandCopy(html: string) {
  const container = document.createElement('div')
  container.style.position = 'fixed'
  container.style.left = '-99999px'
  container.style.top = '0'
  container.style.opacity = '0'
  document.body.appendChild(container)

  const editable = document.createElement('div')
  editable.contentEditable = 'true'
  editable.innerHTML = html
  container.appendChild(editable)

  const range = document.createRange()
  range.selectNodeContents(editable)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)

  try {
    document.execCommand('copy')
  } finally {
    sel?.removeAllRanges()
    container.remove()
  }
}
